const OVERVIEW_CACHE_PREFIX = 'ssos:overview:cache:';
const LEGACY_OVERVIEW_CACHE_KEY = 'ssos:overview:cache';
const OVERVIEW_CACHE_VERSION = 1;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function overviewCacheKey(userId: number | string): string {
  return `${OVERVIEW_CACHE_PREFIX}${userId}`;
}

export interface OverviewCacheEntry<T> {
  data: T;
  fetchedAt: number | null;
}

export function readOverviewCache<T>(userId: number | string): T | null {
  return readOverviewCacheEntry<T>(userId)?.data ?? null;
}

export function readOverviewCacheEntry<T>(userId: number | string): OverviewCacheEntry<T> | null {
  const key = overviewCacheKey(userId);

  try {
    localStorage.removeItem(LEGACY_OVERVIEW_CACHE_KEY);
  } catch {
    // Storage can be unavailable while discarding the legacy flat cache.
  }

  try {
    const cached = localStorage.getItem(key);
    if (cached === null) return null;

    const envelope: unknown = JSON.parse(cached);
    if (
      !isObject(envelope) ||
      envelope.v !== OVERVIEW_CACHE_VERSION ||
      envelope.user !== userId ||
      !isObject(envelope.data) ||
      !isObject(envelope.data.counts) ||
      !isObject(envelope.data.finance)
    ) {
      localStorage.removeItem(key);
      return null;
    }

    return {
      data: envelope.data as T,
      fetchedAt: typeof envelope.fetchedAt === 'number' && Number.isFinite(envelope.fetchedAt)
        ? envelope.fetchedAt : null,
    };
  } catch {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage can also become unavailable while discarding a bad entry.
    }
    return null;
  }
}

export function writeOverviewCache(userId: number | string, value: unknown, fetchedAt: number | null = Date.now()): void {
  try {
    localStorage.setItem(
      overviewCacheKey(userId),
      JSON.stringify({ v: OVERVIEW_CACHE_VERSION, user: userId, data: value, fetchedAt }),
    );
  } catch {
    // Storage can be unavailable in private browsing or at quota.
  }
}

export function clearOverviewCaches(): void {
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(OVERVIEW_CACHE_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // A privacy setting can make even enumerating storage throw.
  }
}

/** Legacy caches have no trustworthy fetch time; never invent one. */
export function overviewRefreshNotice(failed: boolean, hasData: boolean, fetchedAt: number | null): string | null {
  if (!failed) return null;
  if (!hasData) return "Couldn't refresh. No cached data available.";
  if (fetchedAt === null) return "Couldn't refresh. Showing cached data from an unknown time.";
  const time = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Melbourne', day: 'numeric', month: 'short',
    year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(new Date(fetchedAt));
  return `Couldn't refresh. Showing cached data from ${time}.`;
}
